import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import CategoryBrowser from '../../components/Landing/CategoryBrowser'
import TemplateGrid from '../../components/Landing/TemplateGrid'
import { useAuth } from '../../contexts/AuthContext'
import { categoryAPI } from '../../services/api'

const AllTemplatesPage = () => {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [categories, setCategories] = useState([])
  
  // Filter state
  const [filters, setFilters] = useState({
    type: '',                    // '' | 'image' | 'video'
    is_occasion_applicable: '',  // '' | 'true' | 'false'
    occasion_start_date: '',     // '' | 'YYYY-MM-DD'
    has_sample: '',              // '' | 'true' | 'false'
    is_public: '',               // '' | 'true' | 'false'
    is_premium: '',              // '' | 'true' | 'false'
    stage: '',                   // '' | 'pending' | 'submitted' | 'approved' | 'rejected'
    category_id: '',             // '' | category UUID
    order_by: 'created_at',      // 'created_at' | 'updated_at' (always sorted descending)
  })

  const isAdminOrManager = user?.role === 'ADMIN' || user?.role === 'MANAGER'

  // Fetch categories for the category dropdown filter
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryAPI.getAll({ type: 'template', child: false })
        if (response.data.success) {
          const data = Array.isArray(response.data.data) ? response.data.data : response.data.data?.categories || []
          const sorted = data.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          setCategories(sorted)
        }
      } catch (err) {
        console.error('Error fetching categories for filter:', err)
      }
    }
    fetchCategories()
  }, [])

  // Read search query from URL params on mount and when params change
  useEffect(() => {
    const searchParam = searchParams.get('search')
    if (searchParam) {
      setSearchQuery(searchParam)
    } else {
      setSearchQuery('')
    }
  }, [searchParams])

  const handleCategorySelect = (category) => {
    setSelectedCategory(category)
    // Clear category_id filter when using category browser
    if (category !== 'all') {
      setFilters(prev => ({ ...prev, category_id: '' }))
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    const next = new URLSearchParams(searchParams)
    if (searchQuery.trim()) {
      next.set('search', searchQuery.trim())
    } else {
      next.delete('search')
    }
    setSearchParams(next, { replace: false })
  }

  const handleSearchChange = (value) => {
    setSearchQuery(value)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    const next = new URLSearchParams(searchParams)
    next.delete('search')
    setSearchParams(next, { replace: false })
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    // If category_id filter is used, reset the category browser selection
    if (key === 'category_id' && value) {
      setSelectedCategory('all')
    }
  }

  const handleClearFilters = () => {
    setFilters({
      type: '',
      is_occasion_applicable: '',
      occasion_start_date: '',
      has_sample: '',
      is_public: '',
      is_premium: '',
      stage: '',
      category_id: '',
      order_by: 'created_at',
    })
  }

  // Count active filters (order_by is a sort, not a filter, so it's excluded)
  const activeFilterCount = Object.entries(filters)
    .filter(([key, v]) => key !== 'order_by' && v !== '')
    .length

  // Build filters object for TemplateGrid (only include non-empty values)
  const activeFilters = {}
  if (filters.type) activeFilters.type = filters.type
  if (filters.is_occasion_applicable) activeFilters.is_occasion_applicable = filters.is_occasion_applicable === 'true'
  if (filters.occasion_start_date) activeFilters.occasion_start_date = filters.occasion_start_date
  if (filters.has_sample) activeFilters.has_sample = filters.has_sample === 'true'
  if (filters.is_public) activeFilters.is_public = filters.is_public === 'true'
  if (filters.is_premium) activeFilters.is_premium = filters.is_premium === 'true'
  if (filters.stage) activeFilters.stage = filters.stage
  if (filters.category_id) activeFilters.category_id = filters.category_id
  // Sort: always descending; default to created_at
  activeFilters.order_by = filters.order_by || 'created_at'
  activeFilters.order_desc = true

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl p-8 text-white relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold mb-4">
                  All Templates
                </h1>
                <p className="text-purple-100 text-lg mb-6">
                  Browse and discover professional templates from our entire collection
                </p>
                
                {/* Search Bar */}
                <form onSubmit={handleSearchSubmit} className="max-w-md">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search templates..."
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="w-full px-4 py-3 pl-12 pr-12 text-gray-700 bg-white/90 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent placeholder-gray-400"
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                      <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="absolute inset-y-0 right-10 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                    <button
                      type="submit"
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </form>
              </div>
              
              {/* View Toggle */}
              <div className="flex bg-white/20 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          {/* Background Pattern */}
          <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width="200" height="200" fill="url(#grid)" />
            </svg>
          </div>
        </div>
      </div>

      {/* Filter Toggle Button */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
            showFilters || activeFilterCount > 0
              ? 'bg-purple-50 border-purple-300 text-purple-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span className="font-medium">Filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>

        {activeFilterCount > 0 && (
          <button
            onClick={handleClearFilters}
            className="text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Order By (sort) - always descending */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Order By</label>
              <select
                value={filters.order_by}
                onChange={(e) => handleFilterChange('order_by', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="created_at">Created At (newest)</option>
                <option value="updated_at">Updated At (newest)</option>
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Type</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="">All Types</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </div>

            {/* Stage Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Stage</label>
              <select
                value={filters.stage}
                onChange={(e) => handleFilterChange('stage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="">All Stages</option>
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Is Public Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Visibility</label>
              <select
                value={filters.is_public}
                onChange={(e) => handleFilterChange('is_public', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="">All</option>
                <option value="true">Public</option>
                <option value="false">Private</option>
              </select>
            </div>

            {/* Is Premium Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Premium</label>
              <select
                value={filters.is_premium}
                onChange={(e) => handleFilterChange('is_premium', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="">All</option>
                <option value="true">Premium</option>
                <option value="false">Free</option>
              </select>
            </div>

            {/* Occasion Applicable Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Occasion Applicable</label>
              <select
                value={filters.is_occasion_applicable}
                onChange={(e) => handleFilterChange('is_occasion_applicable', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>

            {/* Occasion Start Date Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Occasion Start Date</label>
              <input
                type="date"
                value={filters.occasion_start_date}
                onChange={(e) => handleFilterChange('occasion_start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              />
            </div>

            {/* Has Sample Image Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Sample Image</label>
              <select
                value={filters.has_sample}
                onChange={(e) => handleFilterChange('has_sample', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="">All</option>
                <option value="true">Has Sample</option>
                <option value="false">No Sample</option>
              </select>
            </div>

            {/* Category Filter Dropdown */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Category</label>
              <select
                value={filters.category_id}
                onChange={(e) => handleFilterChange('category_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Active filter tags */}
          {activeFilterCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
              {filters.type && (
                <FilterTag label={`Type: ${filters.type}`} onRemove={() => handleFilterChange('type', '')} />
              )}
              {filters.stage && (
                <FilterTag label={`Stage: ${filters.stage}`} onRemove={() => handleFilterChange('stage', '')} />
              )}
              {filters.is_public && (
                <FilterTag label={`Visibility: ${filters.is_public === 'true' ? 'Public' : 'Private'}`} onRemove={() => handleFilterChange('is_public', '')} />
              )}
              {filters.is_premium && (
                <FilterTag label={`Premium: ${filters.is_premium === 'true' ? 'Yes' : 'No'}`} onRemove={() => handleFilterChange('is_premium', '')} />
              )}
              {filters.is_occasion_applicable && (
                <FilterTag label={`Occasion: ${filters.is_occasion_applicable === 'true' ? 'Yes' : 'No'}`} onRemove={() => handleFilterChange('is_occasion_applicable', '')} />
              )}
              {filters.occasion_start_date && (
                <FilterTag label={`Occasion Date: ${filters.occasion_start_date}`} onRemove={() => handleFilterChange('occasion_start_date', '')} />
              )}
              {filters.has_sample && (
                <FilterTag label={`Sample: ${filters.has_sample === 'true' ? 'Has Sample' : 'No Sample'}`} onRemove={() => handleFilterChange('has_sample', '')} />
              )}
              {filters.category_id && (
                <FilterTag 
                  label={`Category: ${categories.find(c => c.id === filters.category_id)?.name || filters.category_id}`} 
                  onRemove={() => handleFilterChange('category_id', '')} 
                />
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-8">
        {/* Category Browser */}
        <CategoryBrowser onCategorySelect={handleCategorySelect} />
        
        {/* Template Grid */}
        <TemplateGrid 
          category={selectedCategory} 
          enableBulkActions={isAdminOrManager} 
          viewMode={viewMode} 
          searchQuery={searchQuery}
          filters={activeFilters}
        />
      </div>
    </div>
  )
}

// Small helper component for filter tags
const FilterTag = ({ label, onRemove }) => (
  <span className="inline-flex items-center bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-1 rounded-full">
    {label}
    <button
      onClick={onRemove}
      className="ml-1.5 text-purple-600 hover:text-purple-900"
    >
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </button>
  </span>
)

export default AllTemplatesPage
